import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectIsStripeSchoolAdmin } from '../../reducers/user.selectors';
import { useCampaign } from '../../utils/campaigns/useCampaign';
import { shouldDisplayHintsAndOnboardingDialogs } from '../../utils/shouldDisplayHintsAndOnboardingDialogs';
import useRouter from '../../utils/useRouter';
import useShouldShowAiOnlyWelcomeDialog from '../AiOnlyWelcomeDialog/useShouldShowAiOnlyWelcomeDialog';
import useShouldShowLBReOnboardingDialog from '../reOnboardingDialog/useShouldShowLBReOnboardingDialog';
import { isOnStripeSchoolAdminArea } from '../StripeSchoolAdmin/isOnStripeSchoolAdminArea';

const useSchoolAdminHint = () => {
	const isStripeSchoolAdmin = useSelector(selectIsStripeSchoolAdmin);
	const { match } = useRouter();
	const { pathname } = match.location;
	const isAiOnlyWelcomeDialogDue = useShouldShowAiOnlyWelcomeDialog();
	const isLBReOnboardingDialogDue = useShouldShowLBReOnboardingDialog();
	const [isClosed, setIsClosed] = useState(false);

	const isEligible =
		isStripeSchoolAdmin &&
		!isOnStripeSchoolAdminArea(pathname) &&
		shouldDisplayHintsAndOnboardingDialogs() &&
		!isAiOnlyWelcomeDialogDue &&
		!isLBReOnboardingDialogDue &&
		!isClosed;

	const { canDisplay, onSeen, onDismiss } = useCampaign('school-admin-hint', isEligible);

	useEffect(() => {
		if (canDisplay) {
			onSeen();
		}
	}, [canDisplay, onSeen]);

	// The hint lasts one page. A navigation that keeps the avatar mounted (a
	// same-page path change) ends it too; it was already counted, so it never
	// returns. A grant that lands after the change records its own pathname.
	const shownOnPathnameRef = useRef<string | null>(null);
	useEffect(() => {
		if (!canDisplay) {
			shownOnPathnameRef.current = null;
			return;
		}
		if (shownOnPathnameRef.current === null) {
			shownOnPathnameRef.current = pathname;
		} else if (shownOnPathnameRef.current !== pathname) {
			setIsClosed(true);
		}
	}, [canDisplay, pathname]);

	// A dismiss alone keeps the active grant, so the hint also turns itself
	// ineligible, which withdraws it.
	const dismiss = useCallback(() => {
		onDismiss();
		setIsClosed(true);
	}, [onDismiss]);

	return { isOpen: canDisplay, dismiss };
};

export default useSchoolAdminHint;
